/**
 * Visão geral da carteira na Composição de Saldo — regras de apresentação dos
 * cards de empresa.
 *
 * HONESTIDADE DO CARD (regra de negócio, não preciosismo de texto):
 * o `status` de cada empresa deriva de `fecha`, que o backend calcula sobre
 * `extract_balancete` — um SNAPSHOT comprovadamente incompleto. Na empresa 856
 * há 79 contas no balancete contra 106 com movimento real: 27 contas têm
 * movimento e NENHUMA linha no snapshot. O snapshot também não tem competência,
 * então o filtro de período não tem efeito sobre ele.
 *
 * Consequência que a UI precisa respeitar: um card verde NÃO prova que a
 * empresa foi conferida — prova apenas que os débitos e créditos PRESENTES NO
 * SNAPSHOT se igualam. Por isso nenhum rótulo/tooltip daqui afirma conferência,
 * conformidade ou ausência de problema ("tudo certo", "conferido", "sem
 * problemas" são proibidos). Rotulamos o que o dado diz — "fecha", "sem
 * movimento no período", "faltam N aberturas" — nunca o que ele garante.
 * Já houve 7 bugs nesta feature em que o número fechava e a informação estava
 * errada; o texto do card é a última linha de defesa contra o oitavo.
 */

/** Status de uma empresa na visão geral (contrato do endpoint). */
export type StatusEmpresa = 'ok' | 'incompleto' | 'divergente' | 'sem_dado';

/** Ordem de exibição dos chips: o que exige atenção vem primeiro. */
export const STATUS_ORDEM: StatusEmpresa[] = ['divergente', 'incompleto', 'ok', 'sem_dado'];

/**
 * Marca de grupo matriz/filiais do card — contrato de `marca_do_grupo` no
 * backend (`grupo_empresas.py`). Só vem quando há o que dizer: mais de um
 * estabelecimento cliente com a mesma raiz de CNPJ, ou a própria empresa é
 * filial (a matriz não é cliente). Matriz sozinha, a maioria da carteira,
 * vem sem marca.
 */
export interface IGrupoDoCard {
  cnpj_raiz: string;
  papel: 'matriz' | 'filial' | null;
  ordem: string | null;
  /** Estabelecimentos clientes com a mesma raiz, contando este. */
  estabelecimentos: number;
  filiais: number;
  tem_matriz: boolean;
}

/**
 * Um item de `itens` do endpoint `.../contabil/balancete/visao-geral`.
 * Nomes exatos do backend (`balancete_visao_geral.py`) — não renomear.
 */
export interface IEmpresaVisaoGeral {
  /** id INTERNO (`tb_empresa.id`) — É ESTE o id usado ao clicar no card. */
  empresa_id: number;
  external_id: number | null;
  nome: string;
  cnpj: string | null;
  uf: string | null;
  cidade: string | null;
  status: StatusEmpresa;
  /** Débito = crédito NO SNAPSHOT. Não é atestado de conferência. */
  fecha: boolean;
  total_debito: number;
  total_credito: number;
  diferenca: number;
  qtd_contas: number;
  qtd_lancamentos: number;
  contas_sem_abertura: number;
  contas_abertura_manual: number;
  contas_saldo_invertido: number;
  /**
   * Grupo matriz/filiais pela raiz do CNPJ (ver `IGrupoDoCard`). Opcional
   * porque o front pode subir antes do backend que o preenche — card sem o
   * campo é card sem marca, nunca card quebrado.
   */
  grupo?: IGrupoDoCard | null;
}

/** Contagem por status da CARTEIRA INTEIRA (não da página). */
export interface IResumoVisaoGeral {
  ok: number;
  incompleto: number;
  divergente: number;
  sem_dado: number;
}

/** Envelope de `GET .../contabil/balancete/visao-geral` (conteúdo de `data`). */
export interface IBalanceteVisaoGeral {
  data_ini: string;
  data_fim: string;
  /** Empresas da carteira. */
  total: number;
  /** Empresas que sobraram após `busca`/`status` (filtro do SERVIDOR). */
  total_filtrado: number;
  resumo: IResumoVisaoGeral;
  itens: IEmpresaVisaoGeral[];
}

/**
 * Quantos cards por página. Pedido do usuário, revisado: era 10, virou 50.
 *
 * Com 637 empresas na carteira, 10 por página dava 64 páginas — achar a
 * empresa que precisa de conferência virava navegação, não trabalho. A 50, a
 * carteira inteira cabe em 13 páginas e, combinado com a ordem por relevância
 * abaixo, o que precisa de atenção cabe na PRIMEIRA.
 *
 * O recorte é feito NO CLIENTE porque o endpoint não pagina — e isso é o
 * correto aqui, não uma limitação contornada: o `resumo` (os chips de contagem
 * por status) tem que refletir a CARTEIRA INTEIRA. Paginar no servidor exigiria
 * uma segunda varredura só para recompor o resumo, e um resumo que mudasse de
 * página em página diria ao usuário que a carteira encolheu.
 */
export const EMPRESAS_POR_PAGINA = 50;

/**
 * Ordena as empresas por RELEVÂNCIA DE CONFERÊNCIA, não por nome.
 *
 * O contador abre esta tela para achar onde há trabalho, e ordem alfabética
 * espalha isso por 13 páginas. Duas chaves, nesta ordem:
 *
 *  1. APONTAMENTOS (`contas_saldo_invertido`) — conta de ativo com saldo
 *     credor, receita com saldo devedor etc. É o que pede conferência, então
 *     vem primeiro mesmo numa empresa pequena.
 *  2. MOVIMENTO (`qtd_lancamentos`) — desempata e, entre empresas sem
 *     apontamento, põe as grandes na frente: é onde um erro custa mais.
 *
 * O nome fecha o desempate para a ordem ser ESTÁVEL: sem ele, duas empresas
 * com os mesmos números trocariam de lugar entre renderizações e a lista
 * pareceria se mexer sozinha.
 *
 * NÃO muta o array recebido (`[...itens]`): ele vem do cache do react-query, e
 * ordenar no lugar corromperia o dado de outros consumidores.
 */
export const ordenarPorRelevancia = (itens: IEmpresaVisaoGeral[]): IEmpresaVisaoGeral[] =>
  [...itens].sort(
    (a, b) =>
      (b.contas_saldo_invertido ?? 0) - (a.contas_saldo_invertido ?? 0) ||
      (b.qtd_lancamentos ?? 0) - (a.qtd_lancamentos ?? 0) ||
      (a.nome ?? '').localeCompare(b.nome ?? ''),
  );

/**
 * Rótulo curto do status, exibido no card.
 *
 * Descreve o DADO, nunca a conferência. "fecha" é afirmação sobre débito ×
 * crédito no snapshot; jamais "conferido"/"tudo certo"/"sem problemas".
 */
export const ROTULO_STATUS: Record<StatusEmpresa, string> = {
  ok: 'fecha',
  incompleto: 'fecha, faltam aberturas',
  divergente: 'não fecha',
  sem_dado: 'sem movimento no período',
};

/** Rótulo do chip de filtro (plural, contagem da carteira). */
export const ROTULO_CHIP: Record<StatusEmpresa, string> = {
  ok: 'Fecham',
  incompleto: 'Faltam aberturas',
  divergente: 'Não fecham',
  sem_dado: 'Sem movimento',
};

/**
 * Explicação do status (tooltip). Diz o que o status SIGNIFICA — e, no caso do
 * verde, diz explicitamente o que ele NÃO significa. Esse "não" é o ponto: sem
 * ele o verde é lido como "empresa conferida", que o dado não sustenta.
 */
export const EXPLICA_STATUS: Record<StatusEmpresa, string> = {
  ok: 'Débitos e créditos do balancete se igualam no período e não há conta sem saldo de abertura. Não significa que a empresa foi conferida: o balancete é um snapshot que pode não conter todas as contas com movimento.',
  incompleto:
    'Débitos e créditos se igualam, mas há contas sem saldo de abertura na base — a composição dessas contas cobre só o movimento do período.',
  divergente:
    'Débito e crédito não se igualam no período: a partida dobrada do balancete não fechou neste recorte.',
  sem_dado:
    'Sem lançamento no período. Não é erro contábil — é ausência de movimento (ou de importação) para este recorte.',
};

/**
 * Cores por status (semântica definida no docstring do endpoint — não inventar
 * outra): vermelho = não fecha, amarelo = fecha mas faltam aberturas,
 * verde = fecha sem pendência de abertura, cinza = sem movimento.
 *
 * `sem_dado` é CINZA de propósito: ausência de movimento não é erro contábil, e
 * pintá-la de vermelho/amarelo faria o usuário caçar problema onde não há.
 */
export const COR_STATUS: Record<StatusEmpresa, { fg: string; bg: string; borda: string }> = {
  ok: { fg: '#16A34A', bg: 'rgba(22, 163, 74, 0.10)', borda: 'rgba(22, 163, 74, 0.35)' },
  incompleto: { fg: '#D97706', bg: 'rgba(217, 119, 6, 0.10)', borda: 'rgba(217, 119, 6, 0.35)' },
  divergente: { fg: '#DC2626', bg: 'rgba(220, 38, 38, 0.10)', borda: 'rgba(220, 38, 38, 0.35)' },
  sem_dado: { fg: '#64748B', bg: 'rgba(100, 116, 139, 0.10)', borda: 'rgba(100, 116, 139, 0.30)' },
};

/**
 * Detalhe secundário do card — a pendência CONCRETA, quando existe.
 *
 * Só falamos de abertura quando ela é a informação do status (`incompleto`):
 * repetir "faltam N aberturas" numa empresa que nem fecha desviaria o olho do
 * problema maior. Em `divergente` o detalhe é a própria diferença D−C.
 * Retorna `null` quando não há nada honesto a acrescentar — melhor silêncio do
 * que encher o card de texto que não informa.
 */
export const detalheStatus = (e: IEmpresaVisaoGeral): string | null => {
  if (e.status === 'sem_dado') return null;
  if (e.status === 'divergente') return 'débito e crédito não se igualam';
  if (e.contas_sem_abertura > 0) {
    return e.contas_sem_abertura === 1
      ? '1 conta sem saldo de abertura'
      : `${e.contas_sem_abertura} contas sem saldo de abertura`;
  }
  return null;
};

/** CNPJ formatado (`00.000.000/0000-00`). Sem valor vira `—`, nunca string vazia. */
export const fmtCnpj = (cnpj: string | null | undefined): string => {
  const d = String(cnpj ?? '').replace(/\D/g, '');
  if (d.length !== 14) return cnpj ? String(cnpj) : '—';
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
};

/** Localidade `Cidade/UF`, tolerando qualquer um dos dois ausente. */
export const fmtLocal = (e: IEmpresaVisaoGeral): string =>
  [e.cidade, e.uf].filter(Boolean).join('/') || '—';

/**
 * Filtra as empresas por texto (nome, id externo ou CNPJ).
 *
 * A busca deixou de ser a porta de entrada da tela e virou FILTRO dos cards;
 * é aplicada no cliente pelo mesmo motivo do status e da paginação (o `resumo`
 * é da carteira inteira).
 *
 * O CNPJ é comparado SÓ EM DÍGITOS: o usuário digita com ou sem máscara, e
 * comparar "12.345" com "12345678000199" nunca casaria. Busca sem nenhum
 * dígito não entra nessa comparação — `includes('')` é sempre verdadeiro e
 * faria qualquer texto casar com a carteira inteira (bug real, pego no
 * review desta mudança).
 */
export const filtrarPorTexto = (
  itens: IEmpresaVisaoGeral[],
  busca: string,
): IEmpresaVisaoGeral[] => {
  const q = busca.trim().toLowerCase();
  if (!q) return itens;
  const qDigitos = q.replace(/\D/g, '');
  return itens.filter(
    e =>
      e.nome.toLowerCase().includes(q) ||
      String(e.external_id ?? '').includes(q) ||
      (qDigitos.length > 0 && (e.cnpj ?? '').replace(/\D/g, '').includes(qDigitos)),
  );
};

/**
 * Filtra as empresas já carregadas por status.
 *
 * Feito no cliente pelo mesmo motivo da paginação: o `resumo` é da carteira
 * inteira, e mandar `status` ao servidor recortaria a lista sem recompor o
 * resumo — os chips passariam a contar o próprio filtro.
 */
export const filtrarPorStatus = (
  itens: IEmpresaVisaoGeral[],
  status: StatusEmpresa | null,
): IEmpresaVisaoGeral[] => (status ? itens.filter(e => e.status === status) : itens);

/** Total de páginas (mínimo 1 — lista vazia ainda é "página 1 de 1"). */
export const totalPaginas = (total: number, porPagina = EMPRESAS_POR_PAGINA): number =>
  Math.max(1, Math.ceil(total / Math.max(1, porPagina)));

/**
 * Recorte da página (1-based). Página fora do intervalo é GRAMPEADA em vez de
 * devolver lista vazia: ao aplicar um filtro estando na página 7, cair numa
 * tela em branco parece bug — o usuário deve ver a última página existente.
 */
export const paginarEmpresas = (
  itens: IEmpresaVisaoGeral[],
  pagina: number,
  porPagina = EMPRESAS_POR_PAGINA,
): IEmpresaVisaoGeral[] => {
  const alvo = Math.min(Math.max(1, pagina), totalPaginas(itens.length, porPagina));
  const ini = (alvo - 1) * porPagina;
  return itens.slice(ini, ini + porPagina);
};

/**
 * O intervalo exibido no rodapé ("mostrando 51–100 de 637").
 *
 * Mora aqui, e não no JSX, porque é a MESMA aritmética de `paginarEmpresas` e
 * precisa concordar com ela. Enquanto estava solto no componente, o primeiro
 * item era `(pagina - 1) * 10 + 1` com o 10 cravado: ao passar para 50 por
 * página, o rodapé da página 2 diria "11–60 de 637" enquanto a grade mostrava
 * as empresas 51 a 100. Nada quebraria — a tela só passaria a mentir o
 * intervalo, e é o tipo de erro que só o usuário percebe.
 *
 * `empresasNaPagina` é o tamanho REAL do recorte, não `EMPRESAS_POR_PAGINA`:
 * na última página ele é menor, e o fim tem que acompanhar.
 */
export const faixaExibida = (
  pagina: number,
  empresasNaPagina: number,
  totalFiltrado: number,
  porPagina = EMPRESAS_POR_PAGINA,
): { primeiro: number; ultimo: number } => {
  if (totalFiltrado === 0 || empresasNaPagina === 0) return { primeiro: 0, ultimo: 0 };
  const primeiro = (Math.max(1, pagina) - 1) * porPagina + 1;
  return { primeiro, ultimo: Math.min(primeiro + empresasNaPagina - 1, totalFiltrado) };
};

const plural = (n: number, um: string, varios: string) => `${n} ${n === 1 ? um : varios}`;

/**
 * Rótulo da marca de grupo no card: "Matriz · 2 filiais", "Filial 0003 ·
 * grupo de 2" ou "Filial 0003 · matriz não é cliente".
 *
 * Mesmo vocabulário da barra do grupo (`grupoEmpresas.ts`), para o usuário ler
 * o card e a barra como a MESMA informação. Só papel e tamanho: o card não
 * lista membros — a barra faz isso ao abrir a empresa. A filial sem matriz na
 * base diz isso em letras: "1 filial" ao lado de um card leria como se a
 * matriz estivesse em algum outro card.
 */
export const rotuloDoGrupo = (g: IGrupoDoCard): string => {
  if (g.papel === 'matriz') return `Matriz · ${plural(g.filiais, 'filial', 'filiais')}`;
  if (g.papel !== 'filial') return `Grupo de ${g.estabelecimentos}`;
  const filial = `Filial ${g.ordem ?? ''}`.trim();
  return g.tem_matriz
    ? `${filial} · grupo de ${g.estabelecimentos}`
    : `${filial} · matriz não é cliente`;
};

/** Raiz do CNPJ mascarada (`37.391.869`). */
const fmtRaiz = (raiz: string): string =>
  `${raiz.slice(0, 2)}.${raiz.slice(2, 5)}.${raiz.slice(5, 8)}`;

/**
 * Explicação da marca (tooltip): de onde ela vem (raiz do CNPJ) e o que fazer
 * com ela. A dica de buscar pela raiz é VERDADEIRA por construção — a busca
 * compara os dígitos do CNPJ (`filtrarPorTexto`), e a raiz é um prefixo deles;
 * há teste amarrando as duas.
 */
export const explicaGrupo = (g: IGrupoDoCard): string => {
  const raiz = fmtRaiz(g.cnpj_raiz);
  const base =
    g.estabelecimentos > 1
      ? `Mesma raiz de CNPJ (${raiz}): ${g.estabelecimentos} estabelecimentos são clientes do escritório. Busque "${g.cnpj_raiz}" para ver só o grupo; abra a empresa para trocar entre matriz e filiais.`
      : `Raiz de CNPJ ${raiz}: este é o único estabelecimento cliente.`;
  return g.tem_matriz ? base : `${base} A matriz (0001) não é cliente.`;
};
